/**
 * PageTagger — "Which pages are which?" (docs/features/order-to-invoice/87_aw_split_combined_file_plan.md §6): one
 * file sent for every part of an item, shown as a grid of page pictures, each with its number, its size and a tag
 * menu (the part labels, then "Leave out"). A page tagged Leave out is dimmed and labelled "Left out". The summary
 * sentence, the page sizes, the parts and the leave-out word are the read's (`PendingCombinedFile`); the person
 * checks the tags and confirms, and nothing is split before that.
 *
 * Page pictures are the family's JPEGs at the URLs the read carried. The download is auth-gated (a header, not a
 * cookie), so each is fetched through the adapter and shown from an object URL, never pointed at by `<img src>`.
 *
 * The staff order Artwork step's dialog and the basket's slots both lay this out; the tags' state is
 * shared-core's `useCombinedFileTagging`. When *Split into files* cannot run (`blocked_reason`, the command's own
 * words) the button is off and the reason is shown beside it; Cancel still works.
 */
import { useEffect, useId, useState } from "react";
import type { PendingCombinedFile } from "@shared-core/data/api-types";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  LEFT_OUT,
  splitLabel,
  tagToValue,
  useCombinedFileTagging,
  valueToTag,
  type PageTag,
  type SplitAssignment,
} from "@shared-core/domain/combined-file-tagging";
import { Button } from "../../primitives/button";
import { cn } from "../../primitives/utils";

/** Where a refusal shows: a banner summary, and the message beside the grid it is about. */
export interface TaggingRefusal {
  banner: string | null;
  pages: string | null;
}

/** A page's picture, fetched with the caller's auth; a number tile while it loads or if it has none. */
export function PageThumbnail({
  url,
  pageNo,
  className,
}: {
  url: string | null;
  pageNo: number;
  className?: string;
}) {
  const adapter = useDataAdapter();
  const [src, setSrc] = useState<string | null>(null);
  const [isBroken, setIsBroken] = useState(false);
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    adapter
      .downloadArtworkFamilyFile(url)
      .then(({ blob }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setIsBroken(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [adapter, url]);
  return (
    <div
      className={cn(
        "relative flex aspect-[3/4] items-center justify-center overflow-hidden rounded border bg-muted",
        className,
      )}
    >
      {src && !isBroken ? (
        <img
          src={src}
          alt={`Page ${pageNo}`}
          className="size-full object-contain"
          onError={() => setIsBroken(true)}
        />
      ) : (
        <span
          aria-hidden
          className="text-2xl font-medium tabular-nums text-muted-foreground"
        >
          {pageNo}
        </span>
      )}
    </div>
  );
}

const selectClass =
  "h-8 w-full rounded-md border border-input bg-input-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";

/**
 * The grid, the summary and the buttons. `leftOutBadge` is what a dimmed page is labelled ("Left out" for staff).
 * `onSplit` is the person's confirmation; `onCancel` abandons the step (the file is discarded).
 */
export function PageTaggingStep({
  pending,
  busy,
  refusal,
  leftOutBadge = "Left out",
  onSplit,
  onCancel,
}: {
  pending: PendingCombinedFile;
  busy: boolean;
  refusal: TaggingRefusal | null;
  leftOutBadge?: string;
  onSplit: (request: SplitAssignment) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const { tags, tag, request, untagged, summary } =
    useCombinedFileTagging(pending);
  const pagesError = refusal?.pages ?? null;
  const blockedReason = pending.blocked_reason ?? null;
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (untagged.length === 0 && !blockedReason) onSplit(request);
      }}
    >
      <p
        data-testid="tagging-summary"
        className={cn(
          "text-sm",
          pending.outcome === "unsure" && "font-medium text-amber-800",
        )}
      >
        {pending.summary}
      </p>
      <p className="text-xs text-muted-foreground">{pending.file_name}</p>
      {refusal?.banner && (
        <p role="alert" className="text-sm text-red-600">
          {refusal.banner}
        </p>
      )}
      <fieldset
        aria-describedby={pagesError ? `${id}-pages-error` : undefined}
        className="space-y-2"
      >
        <legend className="sr-only">Pages</legend>
        <ol className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {pending.pages.map((page) => {
            const pageTag: PageTag = tags[page.page_no] ?? null;
            const isLeftOut = pageTag === LEFT_OUT;
            return (
              <li
                key={page.page_no}
                data-page={page.page_no}
                data-tag={pageTag ?? ""}
                className="space-y-1"
              >
                <div className="relative">
                  <PageThumbnail
                    url={page.thumbnail_url}
                    pageNo={page.page_no}
                    className={cn(isLeftOut && "opacity-40")}
                  />
                  {isLeftOut && (
                    <span className="absolute inset-x-0 bottom-1 mx-auto w-fit rounded bg-background/90 px-1.5 text-xs font-medium">
                      {leftOutBadge}
                    </span>
                  )}
                </div>
                <p className="text-xs">
                  <span className="font-medium">Page {page.page_no}</span>
                  <span className="text-muted-foreground">
                    {" "}
                    · {page.size_label}
                  </span>
                </p>
                <select
                  className={selectClass}
                  aria-label={`Tag for page ${page.page_no}`}
                  aria-invalid={
                    pagesError && pageTag === null ? true : undefined
                  }
                  disabled={busy}
                  value={tagToValue(pageTag)}
                  onChange={(e) =>
                    tag(page.page_no, valueToTag(e.target.value))
                  }
                >
                  <option value="">Not tagged</option>
                  {pending.parts.map((part) => (
                    <option key={part.part_key} value={part.part_key}>
                      {part.label}
                    </option>
                  ))}
                  <option value={LEFT_OUT}>{pending.left_out_label}</option>
                </select>
              </li>
            );
          })}
        </ol>
        {pagesError && (
          <p id={`${id}-pages-error`} className="text-sm text-red-600">
            {pagesError}
          </p>
        )}
      </fieldset>
      {summary.length > 0 && (
        <p className="text-xs text-muted-foreground" aria-live="polite">
          {summary.map((s) => `${s.label} ${s.pages}`).join(" · ")}
        </p>
      )}
      {untagged.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {untagged.length === 1 ? "Page" : "Pages"} {untagged.join(", ")}{" "}
          {untagged.length === 1 ? "has" : "have"} no tag yet: choose a part, or{" "}
          {pending.left_out_label.toLowerCase()}.
        </p>
      )}
      {blockedReason && (
        <p
          data-testid="split-blocked-reason"
          role="status"
          className="text-sm text-amber-800"
        >
          {blockedReason}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={busy || untagged.length > 0 || blockedReason !== null}
        >
          {splitLabel(request)}
        </Button>
      </div>
    </form>
  );
}
